import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for existing session on mount
    const checkSession = async () => {
      try {
        const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          setError(sessionError.message);
        } else if (currentSession) {
          setSession(currentSession);
          setUser(currentSession.user);
          // Set JWT in localStorage (can be accessed by frontend, use httpOnly cookie via backend)
          localStorage.setItem('supabase_jwt', currentSession.access_token);
        }
      } catch (err) {
        setError(err.message || 'Failed to check session');
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      try {
        if (currentSession) {
          setSession(currentSession);
          setUser(currentSession.user);
          localStorage.setItem('supabase_jwt', currentSession.access_token);
        } else {
          setSession(null);
          setUser(null);
          localStorage.removeItem('supabase_jwt');
        }
        setError(null);
      } catch (err) {
        setError(err.message || 'Auth state change error');
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Starting login for:', email);
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        console.error('Supabase signin error:', signInError);
        const errorMsg = signInError.message || 'Login failed';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      if (data.session) {
        console.log('Login successful, user:', data.session.user?.id);
        setSession(data.session);
        setUser(data.session.user);
        localStorage.setItem('supabase_jwt', data.session.access_token);
        return { success: true, user: data.session.user };
      }
    } catch (err) {
      console.error('Login error:', err);
      let errorMsg = 'Login failed. ';
      
      if (err instanceof TypeError && err.message.includes('fetch')) {
        errorMsg += 'Unable to reach authentication service. Check your internet connection and Supabase configuration.';
      } else if (err.message) {
        errorMsg += err.message;
      } else {
        errorMsg += 'Please check your connection and try again.';
      }
      
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/student-dashboard`,
        },
      });

      if (oauthError) {
        const errorMsg = oauthError.message || 'Google sign-in failed';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      // For OAuth, Supabase will redirect; return success to allow UI state updates
      return { success: true, data };
    } catch (err) {
      const errorMsg = err.message || 'Google sign-in failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      // Do not stop loading here if redirect occurs; but in case of popup flows it helps
      setIsLoading(false);
    }
  };

  const register = async (email, password, userData = {}) => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('Starting registration for:', email);
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData, // Store additional user data in auth metadata
        },
      });

      if (signUpError) {
        console.error('Supabase signup error:', signUpError);
        const errorMsg = signUpError.message || 'Registration failed';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      console.log('Registration successful, user:', data.user?.id);
      if (data.user) {
        // Create profile entry with display name and default preferences
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: data.user.id,
            display_name: userData.displayName || '',
            grade_level: userData.gradeLevel || '',
            email_notifications: true,
            workshop_reminders: true,
            deadline_alerts: true,
            weekly_digest: false,
          }, { onConflict: 'id' });

        if (profileError) {
          console.error('Profile creation error:', profileError);
          // Continue even if profile creation fails
        }

        // Note: User may need to confirm email before session is created
        if (data.session) {
          setSession(data.session);
          setUser(data.session.user);
          localStorage.setItem('supabase_jwt', data.session.access_token);
        }
        return { success: true, user: data.user, requiresEmailConfirmation: !data.session };
      }
    } catch (err) {
      console.error('Registration error:', err);
      let errorMsg = 'Registration failed. ';
      
      if (err instanceof TypeError && err.message.includes('fetch')) {
        errorMsg += 'Unable to reach authentication service. Check your internet connection and Supabase configuration.';
      } else if (err.message) {
        errorMsg += err.message;
      } else {
        errorMsg += 'Please check your connection and try again.';
      }
      
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { error: signOutError } = await supabase.auth.signOut();

      if (signOutError) {
        setError(signOutError.message);
        return { success: false, error: signOutError.message };
      }

      setSession(null);
      setUser(null);
      localStorage.removeItem('supabase_jwt');
      return { success: true };
    } catch (err) {
      const errorMsg = err.message || 'Logout failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email) => {
    setIsLoading(true);
    setError(null);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) {
        setError(resetError.message);
        return { success: false, error: resetError.message };
      }

      return { success: true, message: 'Password reset email sent. Check your inbox.' };
    } catch (err) {
      const errorMsg = err.message || 'Password reset failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    user,
    session,
    isLoading,
    error,
    login,
    register,
    logout,
    resetPassword,
    loginWithGoogle,
    isAuthenticated: !!session,
  };
};
